"use client";

import { useEffect, useRef, useState } from "react";
import { MatchActionBar } from "./MatchActionBar";

export interface TournamentNavbarBreadcrumb {
  label: string;
  href: string;
}

interface TournamentNavbarProps {
  tournamentSlug: string;
  tournamentName: string;
  /** Tournament ID for fetching user team status */
  tournamentId: string;
  /** Optional breadcrumb items (home, tournaments) */
  breadcrumbItems?: TournamentNavbarBreadcrumb[];
}

/**
 * Action-bar carrier under the tournament header.
 *
 * The tournament nav tabs themselves were moved into `DesktopNav` (row 2)
 * and `MobileNav` (level-1 menu) as part of the header revamp. What remains
 * here is the `MatchActionBar` — a sticky bar that surfaces a pending match
 * action (challenge accepted, result needed, etc.) just under whichever
 * header is active.
 *
 * Renders nothing when MatchActionBar has no pending action to show, but the
 * wrapper still measures its height (0 in that case) and publishes it to
 * `--tournament-nav-h` so chat-page height math stays correct.
 */
export function TournamentNavbar({ tournamentId }: TournamentNavbarProps) {
  // Tracks the bar's actual height so the sibling spacer (and any consumer
  // reading `--tournament-nav-h`) keeps in sync as the action appears.
  const navbarRef = useRef<HTMLDivElement>(null);
  const [navbarHeight, setNavbarHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = navbarRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const h = entry.contentRect.height;
      setNavbarHeight(h);
      document.documentElement.style.setProperty(
        "--tournament-nav-h",
        `${h}px`,
      );
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--tournament-nav-h");
    };
  }, []);

  return (
    <>
      <div
        ref={navbarRef}
        className="fixed left-0 right-0 z-40 top-[var(--mobile-nav-h,98px)] md:top-[var(--desktop-nav-h,100px)]"
      >
        <MatchActionBar tournamentId={tournamentId} />
      </div>
      {/* Spacer matches the bar's measured height so content doesn't slide
          under it on chat / scroll-locked pages. */}
      <div
        aria-hidden="true"
        style={navbarHeight != null ? { height: navbarHeight } : undefined}
        className={navbarHeight == null ? "h-0" : undefined}
      />
    </>
  );
}
